import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../../components/feature/Header';
import CommentCard from '../../forum/post/components/CommentCard';
import CommentForm from '../../forum/post/components/CommentForm';
import ReportModal from '../../../components/base/ReportModal';
import ShareModal from '../../../components/base/ShareModal';
import AuthModal from '../../../components/feature/AuthModal';

interface Post {
  id: string;
  title: string;
  content: string;
  content_html?: string;
  author_id: string;
  category_id: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  updated_at: string;
  tags?: string[];
  images?: string[];
  metadata?: any;
  status: string;
  user_profiles?: {
    id: string;
    username: string;
    avatar_url?: string;
    avatar_id?: string;
    reputation: number;
    bio?: string;
  } | null;
  discussion_categories?: {
    id: string;
    name: string;
    color: string;
    description?: string;
  } | null;
}

interface Comment {
  id: string;
  content: string;
  author_id: string;
  post_id: string;
  parent_id?: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  user_profiles?: {
    username: string;
    avatar_url?: string;
    avatar_id?: string;
    reputation: number;
  } | null;
}

export default function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Updated ID validation function to accept numeric IDs, UUIDs, and mock IDs
  const isValidId = (id: string) => {
    // Check if it's a mock ID (starts with "mock-")
    if (id.startsWith('mock-')) {
      return true;
    }
    // Check if it's a numeric ID (for compatibility with existing data)
    if (/^\d+$/.test(id)) {
      return true;
    }
    // Check if it's a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const handleSignInClick = () => {
    setShowAuthModal(true);
  };

  useEffect(() => {
    if (id) {
      // Validate ID format before making database calls
      if (!isValidId(id)) {
        console.error('Invalid post ID format:', id);
        navigate('/forum');
        return;
      }
      
      fetchPost();
      fetchComments();
      if (user) {
        fetchUserVote();
      }
    } else {
      navigate('/forum');
    }
  }, [id, user]);

  const fetchPost = async () => {
    if (!id || !isValidId(id)) {
      navigate('/forum');
      return;
    }

    try {
      // Check if it's a numeric ID or mock ID (mock data) or UUID (database)
      if (/^\d+$/.test(id) || id.startsWith('mock-')) {
        // Handle numeric IDs and mock IDs with mock data
        const mockPost: Post = {
          id: id,
          title: `Community Discussion #${id.replace('mock-', '')}`,
          content: `This is a detailed discussion post with ID ${id}. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.\n\nDuis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.\n\nSed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.`,
          content_html: `<p>This is a detailed discussion post with ID ${id}. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p><p>Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p><p><strong>Key Points:</strong></p><ul><li>Community engagement and discussion</li><li>Sharing experiences and insights</li><li>Building connections with fellow members</li></ul>`,
          author_id: 'mock-author-id',
          category_id: 'general',
          upvotes: Math.floor(Math.random() * 50) + 10,
          downvotes: Math.floor(Math.random() * 10),
          created_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          tags: ['community', 'discussion', 'general'],
          status: 'published',
          user_profiles: {
            id: 'mock-author-id',
            username: `CommunityMember${id.replace('mock-', '')}`,
            avatar_id: `avatar-${id}`,
            reputation: Math.floor(Math.random() * 1000) + 100,
            bio: 'Active community member and discussion enthusiast'
          },
          discussion_categories: {
            id: 'general',
            name: 'General Discussion',
            color: '#3B82F6',
            description: 'General community discussions and topics'
          }
        };
        setPost(mockPost);
        setLoading(false);
        return;
      }

      // Handle UUID format - query database
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles (
            id,
            username,
            avatar_url,
            avatar_id,
            reputation,
            bio
          ),
          discussion_categories (
            id,
            name,
            color,
            description
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }
      setPost(data);
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/forum');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    if (!id || !isValidId(id)) return;

    try {
      // Check if it's a numeric ID or mock ID (mock data) or UUID (database)
      if (/^\d+$/.test(id) || id.startsWith('mock-')) {
        // Generate mock comments for numeric IDs and mock IDs
        const mockComments: Comment[] = [
          {
            id: `comment-${id}-1`,
            content: 'Great discussion topic! I really appreciate the insights shared here. This kind of community engagement is what makes our platform special.',
            author_id: 'mock-commenter-1',
            post_id: id,
            upvotes: Math.floor(Math.random() * 20) + 5,
            downvotes: Math.floor(Math.random() * 3),
            created_at: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
            user_profiles: {
              username: 'DiscussionLover',
              avatar_id: 'commenter-1',
              reputation: 450
            }
          },
          {
            id: `comment-${id}-2`,
            content: 'I have a different perspective on this. While I understand the main points, I think we should also consider the broader implications for our community.',
            author_id: 'mock-commenter-2',
            post_id: id,
            upvotes: Math.floor(Math.random() * 15) + 3,
            downvotes: Math.floor(Math.random() * 2),
            created_at: new Date(Date.now() - Math.random() * 12 * 60 * 60 * 1000).toISOString(),
            user_profiles: {
              username: 'ThoughtfulMember',
              avatar_id: 'commenter-2',
              reputation: 320
            }
          },
          {
            id: `comment-${id}-3`,
            content: 'Thanks for starting this conversation! It\'s exactly what we needed to discuss as a community. Looking forward to more insights from everyone.',
            author_id: 'mock-commenter-3',
            post_id: id,
            upvotes: Math.floor(Math.random() * 25) + 8,
            downvotes: Math.floor(Math.random() * 1),
            created_at: new Date(Date.now() - Math.random() * 6 * 60 * 60 * 1000).toISOString(),
            user_profiles: {
              username: 'CommunityBuilder',
              avatar_id: 'commenter-3',
              reputation: 680
            }
          }
        ];
        setComments(mockComments);
        return;
      }

      // Handle UUID format - query database
      const { data, error } = await supabase
        .from('comments')
        .select(`
          id,
          content,
          author_id,
          post_id,
          parent_id,
          upvotes,
          downvotes,
          created_at
        `)
        .eq('post_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch user profiles separately for each comment
      const commentsWithProfiles = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('username, avatar_url, avatar_id, reputation')
            .eq('id', comment.author_id)
            .single();

          return {
            ...comment,
            user_profiles: profile
          };
        })
      );

      setComments(commentsWithProfiles);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchUserVote = async () => {
    if (!id || !isValidId(id) || !user?.id) return;

    try {
      // For numeric IDs and mock IDs, use local state management
      if (/^\d+$/.test(id) || id.startsWith('mock-')) {
        // Check localStorage for user vote on this numeric/mock post
        const savedVote = localStorage.getItem(`vote-${id}-${user.id}`);
        if (savedVote) {
          setUserVote(savedVote as 'up' | 'down');
        }
        return;
      }

      // Handle UUID format - query database
      const { data, error } = await supabase
        .from('post_votes')
        .select('vote_type')
        .eq('post_id', id)
        .eq('user_id', user.id)
        .single();

      if (data) {
        setUserVote(data.vote_type);
      }
    } catch (error) {
      // No vote found, which is fine
    }
  };

  const handleVote = async (voteType: 'up' | 'down') => {
    if (!user || voting || !post) return;

    try {
      setVoting(true);
      
      // For numeric IDs and mock IDs, use local state management
      if (/^\d+$/.test(post.id) || post.id.startsWith('mock-')) {
        const currentVote = localStorage.getItem(`vote-${post.id}-${user.id}`);
        
        if (currentVote === voteType) {
          // Remove vote
          localStorage.removeItem(`vote-${post.id}-${user.id}`);
          setUserVote(null);
        } else {
          // Set new vote
          localStorage.setItem(`vote-${post.id}-${user.id}`, voteType);
          setUserVote(voteType);
        }
        
        // Update local vote counts
        const updatedPost = { ...post };
        if (currentVote === 'up') updatedPost.upvotes--;
        if (currentVote === 'down') updatedPost.downvotes--;
        if (voteType === 'up' && currentVote !== 'up') updatedPost.upvotes++;
        if (voteType === 'down' && currentVote !== 'down') updatedPost.downvotes++;
        setPost(updatedPost);
        setVoting(false);
        return;
      }

      // Handle UUID format - use database
      const { data: existingVote } = await supabase
        .from('post_votes')
        .select('vote_type')
        .eq('post_id', post.id)
        .eq('user_id', user.id)
        .single();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote
          await supabase
            .from('post_votes')
            .delete()
            .eq('post_id', post.id)
            .eq('user_id', user.id);
          setUserVote(null);
        } else {
          // Update vote
          await supabase
            .from('post_votes')
            .update({ vote_type: voteType })
            .eq('post_id', post.id)
            .eq('user_id', user.id);
          setUserVote(voteType);
        }
      } else {
        // Create new vote
        await supabase
          .from('post_votes')
          .insert({
            post_id: post.id,
            user_id: user.id,
            vote_type: voteType
          });
        setUserVote(voteType);
      }

      // Refresh post to get updated vote counts
      fetchPost();
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAvatarUrl = (profile: any) => {
    if (profile?.avatar_url) return profile.avatar_url;
    if (profile?.avatar_id) {
      return `https://public.readdy.ai/ai/img_res/a2552ad7b265eb0615c917cbb26b127f.jpg$%7Bprofile.avatar_id%7D%20professional%203D%20rendered%20clean%20background%20modern%20digital%20art&width=400&height=400&seq=${profile.avatar_id}&orientation=squarish`;
    }
    return null;
  };

  const renderPollData = () => {
    if (!post?.metadata?.options) return null;

    const totalVotes = post.metadata.votes?.reduce((sum: number, count: number) => sum + count, 0) || 0;

    return (
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Poll</h4>
        <div className="space-y-3">
          {post.metadata.options.map((option: string, index: number) => {
            const votes = post.metadata.votes?.[index] || 0;
            const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
            
            return (
              <div key={index} className="relative">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{option}</span>
                  <span className="text-xs text-gray-500">{votes} votes ({percentage.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-3">Total votes: {totalVotes}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-6">The post you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/forum"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Forum
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const netVotes = post.upvotes - post.downvotes;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <Link to="/forum" className="hover:text-blue-600 transition-colors">Forum</Link>
          <i className="ri-arrow-right-s-line"></i>
          {post.discussion_categories && (
            <>
              <Link 
                to={`/forum/${post.discussion_categories.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="hover:text-blue-600 transition-colors"
              >
                {post.discussion_categories.name}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
            </>
          )}
          <span className="text-gray-900">Post Detail</span>
        </nav>

        {/* Main Post */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            {/* Category and Date */}
            <div className="flex items-center gap-3 mb-4">
              {post.discussion_categories && (
                <span 
                  className="px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: post.discussion_categories.color }}
                >
                  {post.discussion_categories.name}
                </span>
              )}
              <span className="text-gray-500 text-sm">{formatDate(post.created_at)}</span>
              {post.updated_at !== post.created_at && (
                <span className="text-gray-400 text-xs">(edited)</span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{post.title}</h1>

            {/* Author Info */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {getAvatarUrl(post.user_profiles) ? (
                  <img 
                    src={getAvatarUrl(post.user_profiles)} 
                    alt={post.user_profiles?.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {post.user_profiles?.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">
                    {post.user_profiles?.username || 'Unknown User'}
                  </div>
                  {post.user_profiles?.reputation !== undefined && (
                    <div className="text-sm text-gray-500">
                      {post.user_profiles.reputation} reputation points
                    </div>
                  )}
                </div>
              </div>

              {/* Action Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-more-line text-lg"></i>
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => {
                        setShowShareModal(true);
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-share-line mr-2"></i>
                      Share Post
                    </button>
                    <button
                      onClick={() => {
                        setShowReportModal(true);
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <i className="ri-flag-line mr-2"></i>
                      Report Post
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="prose max-w-none mb-6">
              {post.content_html ? (
                <div dangerouslySetInnerHTML={{ __html: post.content_html }} />
              ) : (
                <div className="whitespace-pre-wrap text-gray-700">{post.content}</div>
              )}
            </div>

            {/* Images */}
            {post.images && post.images.length > 0 && (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {post.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Post image ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Poll */}
            {renderPollData()}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Vote and Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                {/* Voting */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleVote('up')}
                    disabled={!user || voting}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                      userVote === 'up' 
                        ? 'bg-green-100 text-green-700' 
                        : 'text-gray-500 hover:bg-gray-100'
                    } ${!user ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    <i className="ri-arrow-up-line"></i>
                    <span>{post.upvotes}</span>
                  </button>
                  <button
                    onClick={() => handleVote('down')}
                    disabled={!user || voting}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${
                      userVote === 'down' 
                        ? 'bg-red-100 text-red-700' 
                        : 'text-gray-500 hover:bg-gray-100'
                    } ${!user ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    <i className="ri-arrow-down-line"></i>
                    <span>{post.downvotes}</span>
                  </button>
                </div>

                {/* Net Score */}
                <div className="flex items-center space-x-1 text-sm">
                  <span className="text-gray-500">Score:</span>
                  <span className={`font-medium ${
                    netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {netVotes > 0 ? '+' : ''}{netVotes}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowShareModal(true)}
                  className="flex items-center space-x-1 px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-share-line"></i>
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            {user ? (
              <div className="mb-8">
                <CommentForm 
                  postId={post.id} 
                  onCommentAdded={fetchComments}
                />
              </div>
            ) : (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-gray-600 mb-3">Please sign in to leave a comment</p>
                <button
                  onClick={handleSignInClick}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-6">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <CommentCard 
                    key={comment.id} 
                    comment={comment}
                    onCommentUpdated={fetchComments}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <i className="ri-chat-3-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500">No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        contentType="post"
        contentId={post.id}
        reportedUserId={post.author_id}
        onReportSubmitted={() => {
          // Optional: Show success message
        }}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={post.title}
        url={window.location.href}
      />

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode="signin"
        />
      )}
    </div>
  );
}
